/* eslint-disable @wyz/module/no-relative-import */
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, Flex, FormContainer, Icon, notify } from '@wyz/ui';
import { useModalState } from '@wyz/utils';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { TransportItemImportModal } from '~/common/components';
import { Trans, useApplicationContext, useI18n } from '~/common/context';
import { Validations } from '~/common/helpers';
import { transportItemFormSchema } from '~/common/schemas';
import { TransportItemFormData } from '~/common/types';
import { useManageTransportItems } from '~/container-order/api';
import { ContainerTracking } from '~/container-order/helpers';
import { usePurchaseErrorTranslation } from '~/common/hooks';
import type { IContainerOrder } from '~/common/types';
import { ViewDisplay } from './transport-item-view';
import { WriteDisplay } from './transport-item-write';

type TransportItemMode = 'READ_ONLY' | 'WRITE';

type TransportItemContextValue = {
  mode: TransportItemMode;
  item: IContainerOrder['resolvedItems'][number];
  order: Pick<IContainerOrder, 'id' | 'updatedFieldsByItemId'>;
  currency?: string;
  importModalState: string | undefined;
  onEditToggle: () => void;
  onCancelEdit: () => void;
  openImportModal: () => void;
  closeImportModal: () => void;
  isUpdating: boolean;
};

const TransportItemContext = React.createContext<
  TransportItemContextValue | undefined
>(undefined);

const useTransportItemContext = () => {
  const context = React.useContext(TransportItemContext);
  if (!context) {
    throw new Error(
      'TransportItem compound components must be used within TransportItem',
    );
  }
  return context;
};

type TransportItemProps = {
  item: IContainerOrder['resolvedItems'][number];
  order: Pick<IContainerOrder, 'id' | 'updatedFieldsByItemId'>;
  currency?: string;
  children: React.ReactNode;
};

const TransportItem: React.FC<TransportItemProps> = ({
  item,
  order,
  currency,
  children,
}) => {
  const { t } = useI18n();
  const { resolveErrorKey } = usePurchaseErrorTranslation();
  const { auth } = useApplicationContext();
  const [mode, setMode] = React.useState<TransportItemMode>('READ_ONLY');
  const [importModalState, importModalHandlers] =
    useModalState<'IMPORT_MODAL'>();

  const methods = useForm<TransportItemFormData>({
    resolver: zodResolver(transportItemFormSchema),
    defaultValues: {
      reference: item.reference || '',
      status: item.status,
      deliveryDate: item.deliveryDate ? new Date(item.deliveryDate) : undefined,
    },
  });
  const manageTransportItems = useManageTransportItems({
    context: { userId: auth.userId as string },
    updateTransportItem: { enabled: true },
  });

  const setError = React.useEffectEvent(methods.setError);
  const onSubmit = React.useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    (formData: TransportItemFormData) => {
      manageTransportItems.updateTransportItem.mutate(
        {
          orderId: order.id,
          itemId: item.uuid,
          body: {
            reference: formData.reference,
            status: formData.status,
            deliveryDate: formData.deliveryDate,
          },
        },
        {
          onSuccess: () => {
            ContainerTracking.trackOrderUpdated(order.id);
            notify.success(<Trans i18nKey='app.common.messages.success' />, {
              id: 'toast-success-transport-item-update',
            });
            setMode('READ_ONLY');
          },
          onError: (error) => {
            notify.error(<Trans i18nKey={resolveErrorKey(error.message)} />, {
              id: 'toast-error-transport-item-update',
            });
            Validations.setFormErrors({
              setError: setError,
              errors: Validations.formatValidationErrors(error.errors, t),
            });
          },
        },
      );
    },
    [
      item.uuid,
      manageTransportItems.updateTransportItem,
      order.id,
      resolveErrorKey,
      t,
    ],
  );

  const reset = React.useEffectEvent(() => {
    methods.reset({
      reference: item.reference || '',
      status: item.status,
      deliveryDate: item.deliveryDate ? new Date(item.deliveryDate) : undefined,
    });
  });

  const handleCancelEdit = React.useCallback(() => {
    setMode('READ_ONLY');
  }, []);

  const handleEditToggle = React.useCallback(() => {
    setMode(mode === 'WRITE' ? 'READ_ONLY' : 'WRITE');
  }, [mode]);

  React.useEffect(() => {
    if (mode === 'WRITE') {
      reset();
    }
  }, [item, mode]);

  const contextValue: TransportItemContextValue = React.useMemo(
    () => ({
      mode,
      item,
      order,
      currency,
      importModalState,
      onEditToggle: handleEditToggle,
      onCancelEdit: handleCancelEdit,
      openImportModal: () => importModalHandlers.open('IMPORT_MODAL'),
      closeImportModal: importModalHandlers.close,
      isUpdating: manageTransportItems.updateTransportItem.isLoading,
    }),
    [
      handleCancelEdit,
      handleEditToggle,
      importModalHandlers,
      importModalState,
      item,
      manageTransportItems.updateTransportItem.isLoading,
      mode,
      order,
      currency,
    ],
  );

  return (
    <TransportItemContext.Provider value={contextValue}>
      <FormContainer
        methods={methods}
        submitHandler={onSubmit}
        formConfiguration={{
          mode: mode,
        }}
      >
        <Card className='mb-3'>{children}</Card>
      </FormContainer>
      <TransportItemModal />
    </TransportItemContext.Provider>
  );
};

const TransportItemContent: React.FC = () => {
  const { mode, item, order, currency } = useTransportItemContext();

  return (
    <Card.Body>
      {mode === 'WRITE' ? (
        <WriteDisplay item={item} currency={currency} />
      ) : (
        <ViewDisplay item={item} order={order} currency={currency} />
      )}
    </Card.Body>
  );
};

const TransportItemActions: React.FC = () => {
  const {
    mode,
    item,
    onCancelEdit,
    onEditToggle,
    openImportModal,
    isUpdating,
  } = useTransportItemContext();

  return (
    <Card.Footer>
      <hr />
      <Flex justifyContent='end' className='space-x-4'>
        <Button
          size='sm'
          variant='secondary'
          id={`import-btn-${item.reference}`}
          onClick={openImportModal}
          disabled={mode === 'WRITE' || isUpdating}
        >
          <Icon icon='upload-line' size='xxs' className='me-1' />
          <Trans i18nKey='app.common.buttons.import' />
        </Button>

        {mode === 'WRITE' ? (
          <>
            <Button
              size='sm'
              variant='secondary'
              onClick={onCancelEdit}
              id={`cancel-btn-${item.reference}`}
              disabled={isUpdating}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            <Button
              size='sm'
              variant='primary'
              type='submit'
              id={`save-btn-${item.reference}`}
              isLoading={isUpdating}
            >
              <Trans i18nKey='app.common.button.accept' />
            </Button>
          </>
        ) : (
          <Button
            size='sm'
            variant='secondary'
            onClick={onEditToggle}
            id={`update-btn-${item.reference}`}
            isLoading={isUpdating}
          >
            <Icon icon='pencil-line' size='xxs' className='me-1' />
            <Trans i18nKey='app.common.buttons.update' />
          </Button>
        )}
      </Flex>
    </Card.Footer>
  );
};

const TransportItemModal: React.FC = () => {
  const { importModalState, closeImportModal, order, item } =
    useTransportItemContext();

  if (importModalState !== 'IMPORT_MODAL') {
    return null;
  }

  return (
    <TransportItemImportModal
      onClose={closeImportModal}
      orderId={order.id}
      item={item}
    />
  );
};

type TransportItemComponent = React.FC<TransportItemProps> & {
  Content: typeof TransportItemContent;
  Actions: typeof TransportItemActions;
};

const TransportItemPublicApi = TransportItem as TransportItemComponent;

TransportItemPublicApi.Content = TransportItemContent;
TransportItemPublicApi.Actions = TransportItemActions;

export default TransportItemPublicApi;
